/*
 *
 *  * Copyright (c) [hoarfroster-frontend] 2026. All Rights Reserved.
 *  *
 *  * Last Modified on Sep 12, 2026 by hoarfroster
 *
 */

import { Component, Suspense, type ReactNode } from 'react';
import './index.css';
import App from './app';
import Spinner from '@components/spinner';
import { ToastProvider, ConfirmProvider } from '@components/ui/feedback';
import { BannerProvider } from './contexts/banner-context';
import { PreferencesProvider } from './utils/preferences-context';

/** Last-resort boundary: a render crash degrades to a message, not a blank page. */
class RenderErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
    state: { error: Error | null } = { error: null };

    static getDerivedStateFromError(error: Error) {
        return { error };
    }

    render() {
        if (this.state.error) {
            return (
                <div className="flex h-full w-full flex-col items-center justify-center gap-2 p-8" role="alert">
                    <p className="text-theme-text text-sm font-semibold">Something went wrong while rendering.</p>
                    <pre className="text-theme-text-muted max-w-2xl overflow-auto text-xs whitespace-pre-wrap">
                        {this.state.error.message}
                    </pre>
                </div>
            );
        }
        return this.props.children;
    }
}

export const Scaffold = () => (
    <RenderErrorBoundary>
        <PreferencesProvider>
            <ToastProvider>
                <ConfirmProvider>
                    <BannerProvider>
                        <Suspense fallback={<SuspenseWrapper />}>
                            <App />
                        </Suspense>
                    </BannerProvider>
                </ConfirmProvider>
            </ToastProvider>
        </PreferencesProvider>
    </RenderErrorBoundary>
);

function SuspenseWrapper() {
    return (
        <div className="flex h-full w-full items-center justify-center">
            <Spinner />
        </div>
    );
}
